import React, { useState } from 'react';
import { LandingNav } from '../components/Nav';
import SiteFooter from '../components/SiteFooter';
import {
  CHAPTER_SECTION_SCROLL_MARGIN_MOTHERHOOD_PX,
} from '../hooks/useChapterSectionNav';
import NovaAvatarImg from '../components/NovaAvatarImg';

// ── SUB-COMPONENTS ───────────────────────────────────────────────

const ActLabel = ({ text }) => (
  <div style={{ display: 'inline-flex', background: 'rgba(155,41,66,0.1)', borderRadius: '20px', padding: '4px 12px', marginBottom: '14px' }}>
    <span style={{ fontSize: '11px', fontWeight: 600, color: 'rgb(125,30,52)' }}>{text}</span>
  </div>
);

const SectionHeader = ({ act, headline, italicLine, subhead }) => (
  <div style={{ textAlign: 'center', marginBottom: '24px' }}>
    <ActLabel text={act} />
    <h2 className="serif" style={{ fontSize: 'clamp(24px, 2.5vw, 28px)', fontWeight: 700, textAlign: 'center', color: 'rgb(17,17,17)', lineHeight: 1.25, marginBottom: '10px' }}>
      {headline}
      <br />
      <em style={{ color: 'rgb(155,41,66)' }}>{italicLine}</em>
    </h2>
    <p style={{ fontSize: '15px', color: 'rgb(122,107,99)', textAlign: 'center', maxWidth: '540px', margin: '0 auto', lineHeight: 1.65 }}>{subhead}</p>
  </div>
);

const FeatCard = ({ emoji, title, desc, isNew, borderColor = 'rgba(155,41,66,0.4)' }) => (
  <div style={{ background: 'rgb(255,253,249)', border: '0.5px solid rgb(232,224,216)', borderRadius: '14px', padding: '18px', flex: 1, display: 'flex', flexDirection: 'column', borderLeft: `3px solid ${borderColor}` }}>
    <div style={{ fontSize: '22px', marginBottom: '10px' }}>{emoji}</div>
    <p style={{ fontSize: '13px', fontWeight: 700, color: 'rgb(17,17,17)', marginBottom: '5px', lineHeight: 1.3 }}>
      {title}
      {isNew && (
        <span style={{ fontSize: '9px', fontWeight: 700, background: 'rgb(245,234,237)', color: 'rgb(125,30,52)', padding: '1px 6px', borderRadius: '8px', marginLeft: '6px', verticalAlign: 'middle' }}>New</span>
      )}
    </p>
    <p style={{ fontSize: '12px', color: 'rgb(95,94,90)', lineHeight: 1.5, margin: 0 }}>{desc}</p>
  </div>
);

const CardHead = ({ title, sub, right }) => (
  <div style={{ padding: '12px 16px', borderBottom: '1.5px solid rgb(155,41,66)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
    <div>
      <p style={{ fontSize: '13px', fontWeight: 600, color: 'rgb(17,17,17)', margin: 0 }}>{title}</p>
      {sub && <p style={{ fontSize: '11px', color: 'rgb(122,107,99)', marginTop: '1px', marginBottom: 0 }}>{sub}</p>}
    </div>
    {right}
  </div>
);

const InsightCard = ({ title, desc, tinted = false }) => (
  <div style={{ background: tinted ? 'rgba(155,41,66,0.1)' : 'white', borderRadius: '10px', padding: '11px', border: tinted ? '0.5px solid rgba(155,41,66,0.2)' : '0.5px solid rgb(232,224,216)' }}>
    <p style={{ fontSize: '11px', fontWeight: 700, color: tinted ? 'rgb(125,30,52)' : 'rgb(17,17,17)', marginBottom: '3px' }}>{title}</p>
    <p style={{ fontSize: '11px', color: tinted ? 'rgb(90,35,50)' : 'rgb(95,94,90)', lineHeight: 1.5, margin: 0 }}>{desc}</p>
  </div>
);

// ── AGE-SPECIFIC CONTENT ─────────────────────────────────────────

const AGE_RANGES = [
  { id: 'newborn', label: '0–3 months', emoji: '🍼' },
  { id: 'infant', label: '4–12 months', emoji: '🧸' },
  { id: 'toddler', label: 'Toddler (1–3)', emoji: '🏃' },
  { id: 'preschool', label: 'Preschool (3–5)', emoji: '🎨' },
  { id: 'school', label: 'School age (6–12)', emoji: '🎒' },
  { id: 'teen', label: 'Teen (13+)', emoji: '🎓' },
];

const RECOVER_CONTENT = {
  newborn: {
    oraName: 'Emma',
    childName: 'Sofia',
    childAge: '6 weeks old',
    childBorn: 'Born Jan 14',
    childDetails: 'C-section · breastfeeding · returning to work in 6 weeks · 6-week OB visit Thursday',
    recovery: 'Incision healing typically complete by week 6. Cleared for most activity after your OB visit Thursday. Pelvic floor PT referral is worth asking for.',
    feeling: 'Logged: overwhelmed 3 days this week, anxious about returning to work, breastfeeding going better. Your mood pattern is worth discussing Thursday — support is available.',
    note: "You have your 6-week OB visit Thursday. I've prepared questions — about returning to work, pelvic floor PT, and the anxiety you've been logging. This appointment should be about you, not just baby.",
    articles: ['What is matrescence — and why no one talks about it', 'PPD vs baby blues — what to know at 6 weeks', 'Going back to work with a 12-week-old — what helps'],
  },
  infant: {
    oraName: 'Sarah',
    childName: 'Liam',
    childAge: '7 months old',
    childBorn: 'Born Aug 3',
    childDetails: 'Vaginal birth · mixed feeding · recently started solids · sleep regression ongoing',
    recovery: '7 months postpartum — your body has largely recovered but pelvic floor and hormonal changes continue. Many moms notice hair loss and energy dips around now.',
    feeling: 'Logged: exhausted from sleep regression, excited about solids milestone, feeling more like yourself. The 7-month mark is often when moms start thinking about their own needs again.',
    note: "Sleep regression at 7–8 months is brutal but temporary. I've noticed you've been logging high exhaustion — want me to look at Liam's sleep patterns and suggest some adjustments?",
    articles: ['The 7-month sleep regression — what actually helps', 'Starting solids safely — what the research says', 'Returning to yourself after the fourth trimester'],
  },
  toddler: {
    oraName: 'Jessica',
    childName: 'Maya',
    childAge: '2 years, 3 months',
    childBorn: 'Born Oct 12',
    childDetails: 'Fully weaned · in daycare · language explosion happening · potty training starting',
    recovery: 'At 2+ years, your physical recovery is long complete — but emotional and identity shifts continue. Many moms report this as when they start reclaiming their sense of self.',
    feeling: "Logged: frustrated by tantrums, proud of Maya's language development, feeling stretched between work and home. The toddler stage is genuinely hard — you're not alone.",
    note: "Maya is in a big language development window right now — the frustration you're logging about tantrums is connected to her wanting to communicate more than she can. I have some strategies that might help.",
    articles: ["Toddler tantrums — what's actually happening in their brain", 'Potty training readiness — signs to look for', 'Identity after motherhood — reclaiming yourself'],
  },
  preschool: {
    oraName: 'Amanda',
    childName: 'Owen',
    childAge: '4 years old',
    childBorn: 'Born Feb 28',
    childDetails: 'Starting pre-K in fall · navigating friendships · big emotions · working mom',
    recovery: 'Years into motherhood now — but Nova still checks in on you, not just Owen. Working mom mental load, identity, and relationship dynamics evolve continuously.',
    feeling: "Logged: anxious about pre-K transition, proud of Owen's social growth, feeling the mental load heavily this week. Pre-K transitions are genuinely stressful for parents too.",
    note: "Owen's pre-K registration opens in 3 weeks. I've found 4 programs near you in Columbus with openings for fall — and prepared questions to ask during tours. Want to see them?",
    articles: ['Pre-K readiness — what actually matters', "Preparing yourself for kindergarten transition (it's emotional)", 'Managing the mental load as a working parent'],
  },
  school: {
    oraName: 'Rachel',
    childName: 'Emma',
    childAge: '8 years old',
    childBorn: 'Born May 5',
    childDetails: '2nd grade · after-school activities · homework battles starting · starting to have opinions',
    recovery: "8 years into motherhood — Nova grows with you. Right now it's about parenting a child with her own perspective, managing school logistics, and making sure you're not lost in the shuffle.",
    feeling: "Logged: frustrated by homework battles, proud of Emma's growing independence, feeling like you're constantly managing logistics. The school age years are the logistical peak.",
    note: "Emma's reading comprehension is a strength based on what you've shared — the homework frustration might be about engagement, not ability. I have some strategies that work well at this age.",
    articles: ['Homework battles — strategies that actually work', 'Supporting emotional development ages 6–12', 'Finding yourself in the school-age years'],
  },
  teen: {
    oraName: 'Linda',
    childName: 'Jake',
    childAge: '15 years old',
    childBorn: 'Born Mar 17',
    childDetails: '10th grade · driving soon · social media challenges · talking less · but still needs you',
    recovery: "15 years into this — and Nova is still here. Teen parenting is one of the loneliest stages because most apps have abandoned you by now. We haven't.",
    feeling: "Logged: worried about Jake's screen time, proud of his independence, missing the closeness you used to have. The distance teens create is developmentally normal — and painful.",
    note: "You mentioned Jake has been quieter lately. Research shows teens who pull away actually need connection more — just differently. I have some communication approaches that work well at 15 without feeling forced.",
    articles: ['How to stay connected to your teenager', 'Social media and teen mental health — what parents can do', "Parenting a teenager — you're not failing, this is just hard"],
  },
};

const TRACK_CONTENT = {
  newborn: {
    chat: [
      { from: 'nova', text: 'Good morning 💚 Sofia is 6 weeks today. How did last night go? I noticed you logged a 2am feed — how are you doing on sleep?' },
      { from: 'user', text: "She slept 3 hours which felt amazing. But I'm worried she's not gaining enough weight." },
      { from: 'nova', text: "3 hours is a win at 6 weeks — trust that. Based on what you logged, Sofia gained 7oz in the past 2 weeks, which is within the normal range for breastfed babies. Your pediatrician visit is next week. Is she having 6+ wet diapers a day?" },
      { from: 'user', text: 'Yes, definitely. Is the 4-month sleep regression real?' },
      { from: 'nova', text: "Very real — around weeks 16–20, sleep cycles mature and many babies wake more frequently. I'll prep you for it before it hits so it doesn't feel like everything fell apart. You're 10 weeks out — I'll check in then." },
    ],
    pills: ['Is this feeding amount normal?', 'When do babies start smiling?', 'How do I survive sleep regression?'],
  },
  infant: {
    chat: [
      { from: 'nova', text: "Hi Sarah 💚 Liam is 7 months today. How's the sleep regression treating you? I noticed 3 wake-ups logged last night." },
      { from: 'user', text: "It's brutal. Also he keeps gagging on purees — is that normal?" },
      { from: 'nova', text: "The gagging is actually protective — it's how babies learn to manage food textures. It looks scarier than it is. True choking is silent; gagging is noisy. At 7 months, some babies do better with soft finger foods than purees. Want to talk through what's working?" },
      { from: 'user', text: 'Yes. Also when does this sleep regression end?' },
      { from: 'nova', text: "The 8-month regression typically lasts 2–6 weeks. Liam is right on schedule. The good news: most babies come out of it sleeping better than before because their sleep cycles have matured. I'll track it with you." },
    ],
    pills: ['Best first foods at 7 months?', 'How do I handle the 8-month regression?', 'Is this rash normal?'],
  },
  toddler: {
    chat: [
      { from: 'nova', text: "Hi Jessica 💚 Maya had a big tantrum day yesterday based on your log. How are you both doing today?" },
      { from: 'user', text: 'Exhausted. She threw a 45-minute tantrum over a cracker. Is this normal?' },
      { from: 'nova', text: "Completely normal at 2 years 3 months — she's in a major language acquisition window, so she has more emotions than words. The mismatch creates frustration that becomes tantrums. You're not doing anything wrong." },
      { from: 'user', text: 'How long does this phase last?' },
      { from: 'nova', text: "Peak tantrum intensity is usually 18 months–3 years, with gradual improvement as language catches up. Maya's language is actually ahead — which means this phase may ease a bit earlier for her. I'll track it with you." },
    ],
    pills: ['Why is my toddler so clingy?', 'When does potty training start?', 'Is this behavior normal at 2?'],
  },
  preschool: {
    chat: [
      { from: 'nova', text: "Hi Amanda 💚 Owen's pre-K search is on your mind this week. How's the process going?" },
      { from: 'user', text: "Overwhelming. I don't even know what to look for in a pre-K." },
      { from: 'nova', text: 'Great question — the research points to a few key things: play-based learning, low child-to-teacher ratio (ideally 8:1 or lower), and teachers who respond warmly to children. Academic drills at age 4 are actually less important than social-emotional development.' },
      { from: 'user', text: 'What questions should I ask on tours?' },
      { from: 'nova', text: "I've built a tour question list for you: how they handle separation anxiety, what a typical day looks like, their approach to behavior, and how they communicate with parents. Want me to send it to you?" },
    ],
    pills: ['Pre-K vs waiting for kindergarten?', 'How do I handle separation anxiety?', 'What are age-appropriate chores at 4?'],
  },
  school: {
    chat: [
      { from: 'nova', text: "Hi Rachel 💚 Emma's in 2nd grade now. How's the homework situation going?" },
      { from: 'user', text: "Battles every night. She says it's boring and refuses to sit still." },
      { from: 'nova', text: "The 'boring' complaint is actually useful information — it often means the work feels too easy (boredom) or too hard (avoidance). At 8, kids need about 20 minutes of homework max. More than that and you're fighting the wrong battle." },
      { from: 'user', text: 'Her teacher wants 45 minutes. Should I push back?' },
      { from: 'nova', text: "Research from homework experts suggests 10 minutes per grade level is the right amount — so 20 minutes at 2nd grade. 45 minutes is counterproductive and increases negative associations with learning. Worth a conversation with her teacher. Want me to help you draft that email?" },
    ],
    pills: ['Is my 8-year-old reading on track?', 'How do I handle friend drama?', 'Screen time guidelines for school age?'],
  },
  teen: {
    chat: [
      { from: 'nova', text: 'Hi Linda 💚 You mentioned Jake has been quieter lately. How are things at home?' },
      { from: 'user', text: "He barely talks to me. I feel like I'm losing him." },
      { from: 'nova', text: "What you're feeling is one of the hardest parts of teen parenting — and incredibly common. Teens pull away to develop their identity, but research shows they still deeply need parental connection. They just need it to look different than it did when they were 8." },
      { from: 'user', text: 'How do I connect with him without pushing him away?' },
      { from: 'nova', text: 'Side-by-side activities work better than face-to-face conversations at this age. Driving somewhere, watching a show together, being in the same room. The car is one of the best places for meaningful conversation — no eye contact, natural pauses. No agenda, just presence.' },
    ],
    pills: ['How do I talk to my teen about mental health?', 'Social media boundaries that actually work?', 'When should I worry vs let go?'],
  },
};

const CONNECT_SPACES = {
  newborn: [
    { emoji: '👶', name: 'Newborns 0–3 Months', desc: 'Fourth trimester · feeding · sleep survival', tag: 'Matched', tagBg: '#F5EAED', tagColor: '#7D1E34' },
    { emoji: '🧠', name: 'PPD & Postpartum Mental Health', desc: 'Safe space · no judgment · real support', tag: 'Support', tagBg: '#E8EEF8', tagColor: '#2A4A8A' },
    { emoji: '💼', name: 'Working Moms — Returning', desc: 'Leave timeline · childcare · pumping at work', tag: 'Matched', tagBg: '#F5EAED', tagColor: '#7D1E34' },
    { emoji: '🍼', name: 'Breastfeeding Support', desc: 'Latch · supply · pumping · combo feeding', tag: 'Matched', tagBg: '#F5EAED', tagColor: '#7D1E34' },
    { emoji: '🌿', name: 'C-Section Recovery', desc: 'Healing · scar care · emotional processing', tag: 'Support', tagBg: '#E8EEF8', tagColor: '#2A4A8A' },
    { emoji: '🌸', name: 'IVF Graduates — New Moms', desc: 'Extra support for moms after fertility treatment', tag: 'Matched', tagBg: '#F5EAED', tagColor: '#7D1E34' },
  ],
  infant: [
    { emoji: '🧸', name: 'Infants 4–12 Months', desc: 'Sleep regressions · solids · crawling milestones', tag: 'Matched', tagBg: '#F5EAED', tagColor: '#7D1E34' },
    { emoji: '🍎', name: 'Starting Solids', desc: 'BLW vs purees · food allergies · what to try first', tag: 'Matched', tagBg: '#F5EAED', tagColor: '#7D1E34' },
    { emoji: '😴', name: 'Sleep Training Support', desc: 'Methods · schedules · what actually works', tag: 'Live now', tagBg: '#F5EAED', tagColor: '#7D1E34' },
    { emoji: '💼', name: 'Working Moms', desc: 'Back at work · pumping · daycare transitions', tag: 'Matched', tagBg: '#F5EAED', tagColor: '#7D1E34' },
    { emoji: '🧠', name: 'Postpartum Mental Health', desc: 'PPD · PPA · identity · ongoing support', tag: 'Support', tagBg: '#E8EEF8', tagColor: '#2A4A8A' },
    { emoji: '🌙', name: 'Night Feeding Club', desc: '3am feeds · you are not alone', tag: 'Live now', tagBg: '#F5EAED', tagColor: '#7D1E34' },
  ],
  toddler: [
    { emoji: '🏃', name: 'Toddler Parents (1–3)', desc: 'Tantrums · sleep · language · potty training', tag: 'Matched', tagBg: '#F5EAED', tagColor: '#7D1E34' },
    { emoji: '🗣️', name: 'Speech & Language Development', desc: 'Milestones · early intervention · when to ask', tag: 'Support', tagBg: '#FEF3E2', tagColor: '#8A5010' },
    { emoji: '💼', name: 'Working Moms', desc: 'Daycare guilt · schedules · the mental load', tag: 'Matched', tagBg: '#F5EAED', tagColor: '#7D1E34' },
    { emoji: '🌙', name: 'Toddler Sleep Struggles', desc: 'Night wakings · nap transitions · bedtime battles', tag: 'Matched', tagBg: '#F5EAED', tagColor: '#7D1E34' },
    { emoji: '🤰', name: 'Expecting Again', desc: 'TTC after baby #1 · spacing · telling your toddler', tag: 'All ages', tagBg: '#EDE8FE', tagColor: '#4A30A0' },
    { emoji: '🧠', name: 'Maternal Mental Health', desc: "Ongoing support — it doesn't end at 6 weeks", tag: 'Support', tagBg: '#E8EEF8', tagColor: '#2A4A8A' },
  ],
  preschool: [
    { emoji: '🎨', name: 'Preschool & Pre-K Parents', desc: 'School readiness · friendships · big emotions', tag: 'Matched', tagBg: '#F5EAED', tagColor: '#7D1E34' },
    { emoji: '💼', name: 'Working Moms', desc: 'School logistics · after-care · career balance', tag: 'Matched', tagBg: '#F5EAED', tagColor: '#7D1E34' },
    { emoji: '🌈', name: 'Raising Emotionally Healthy Kids', desc: 'Big feelings · emotional intelligence · connection', tag: 'All ages', tagBg: '#FEF3E2', tagColor: '#8A5010' },
    { emoji: '🤰', name: 'Planning Baby #2', desc: 'Timing · spacing · telling your preschooler', tag: 'All ages', tagBg: '#EDE8FE', tagColor: '#4A30A0' },
    { emoji: '🧩', name: 'Developmental Concerns', desc: 'When to ask · early intervention · advocacy', tag: 'Support', tagBg: '#E8EEF8', tagColor: '#2A4A8A' },
    { emoji: '🌙', name: 'Sleep & Bedtime Battles', desc: 'Preschool sleep challenges · nightmares · independence', tag: 'Matched', tagBg: '#F5EAED', tagColor: '#7D1E34' },
  ],
  school: [
    { emoji: '🎒', name: 'School Age Parents (6–12)', desc: 'Homework · friendships · activities · independence', tag: 'Matched', tagBg: '#F5EAED', tagColor: '#7D1E34' },
    { emoji: '💼', name: 'Working Moms', desc: 'After-school logistics · school communication · balance', tag: 'Matched', tagBg: '#F5EAED', tagColor: '#7D1E34' },
    { emoji: '🧠', name: "Kids' Mental Health", desc: 'Anxiety · ADHD · school struggles · when to seek help', tag: 'Support', tagBg: '#E8EEF8', tagColor: '#2A4A8A' },
    { emoji: '📱', name: 'Tech & Screen Time', desc: 'Device rules · social media intro · gaming', tag: 'All ages', tagBg: '#FEF3E2', tagColor: '#8A5010' },
    { emoji: '🌈', name: 'Raising Resilient Kids', desc: 'Confidence · failure · emotional regulation', tag: 'All ages', tagBg: '#FEF3E2', tagColor: '#8A5010' },
    { emoji: '🏃', name: 'Sports & Activities Balance', desc: 'Too much · too little · finding the right fit', tag: 'Matched', tagBg: '#F5EAED', tagColor: '#7D1E34' },
  ],
  teen: [
    { emoji: '🎓', name: 'Teen Parents (13+)', desc: 'Communication · independence · mental health', tag: 'Matched', tagBg: '#F5EAED', tagColor: '#7D1E34' },
    { emoji: '💼', name: 'Working Moms with Teens', desc: 'Independence vs availability · career pivots', tag: 'Matched', tagBg: '#F5EAED', tagColor: '#7D1E34' },
    { emoji: '🧠', name: 'Teen Mental Health', desc: 'Anxiety · depression · when to step in', tag: 'Support', tagBg: '#E8EEF8', tagColor: '#2A4A8A' },
    { emoji: '📱', name: 'Social Media & Online Safety', desc: 'Platforms · boundaries · having the conversation', tag: 'All ages', tagBg: '#FEF3E2', tagColor: '#8A5010' },
    { emoji: '🎓', name: 'College Prep Moms', desc: 'Applications · letting go · empty nest approaching', tag: 'Matched', tagBg: '#F5EAED', tagColor: '#7D1E34' },
    { emoji: '🌿', name: 'Moms of Adult Children', desc: 'The transition · friendship · still their mom', tag: 'All ages', tagBg: '#FEF3E2', tagColor: '#8A5010' },
  ],
};

const LIVE_ROOMS = {
  newborn: [
    { color: 'rgb(155,41,66)', name: '3am feed check-in — anyone else awake?', sub: 'Newborns · 18 moms live now' },
    { color: 'rgb(155,41,66)', name: 'Breastfeeding struggles — real talk', sub: 'Newborns · 9 moms live now' },
    { color: 'rgb(155,41,66)', name: '6-week OB visit — what to expect', sub: 'Newborns · Starting in 10 min' },
    { color: 'rgb(136,135,128)', name: 'Returning to work — nervous talk', sub: 'Working Moms · Tomorrow 7pm ET' },
  ],
  infant: [
    { color: 'rgb(155,41,66)', name: '4-month sleep regression survival', sub: 'Infants · 11 moms live now' },
    { color: 'rgb(155,41,66)', name: 'Starting solids Q&A', sub: 'Infants · 7 moms live now' },
    { color: 'rgb(155,41,66)', name: 'Going back to work — checking in', sub: 'Working Moms · 5 moms live now' },
    { color: 'rgb(136,135,128)', name: 'Is this a sleep regression or illness?', sub: 'Infants · Tomorrow 8pm ET' },
  ],
  toddler: [
    { color: 'rgb(155,41,66)', name: 'Tantrum support — real time', sub: 'Toddlers · 14 moms live now' },
    { color: 'rgb(155,41,66)', name: 'Potty training wins & fails', sub: 'Toddlers · 8 moms live now' },
    { color: 'rgb(155,41,66)', name: 'Working mom guilt — open conversation', sub: 'Working Moms · 6 moms live now' },
    { color: 'rgb(136,135,128)', name: 'Toddler sleep transitions', sub: 'Toddlers · Tomorrow 7pm ET' },
  ],
  preschool: [
    { color: 'rgb(155,41,66)', name: 'Pre-K prep — what to expect', sub: 'Preschool · 9 moms live now' },
    { color: 'rgb(155,41,66)', name: 'Big feelings & meltdowns at 4–5', sub: 'Preschool · 6 moms live now' },
    { color: 'rgb(136,135,128)', name: 'Childcare research — comparing options', sub: 'Working Moms · Starting in 30 min' },
    { color: 'rgb(136,135,128)', name: 'When to consider evaluation — open Q&A', sub: 'Support · Tomorrow 7pm ET' },
  ],
  school: [
    { color: 'rgb(155,41,66)', name: 'Homework battles — what actually works', sub: 'School Age · 11 moms live now' },
    { color: 'rgb(155,41,66)', name: 'Friend drama at school age', sub: 'School Age · 7 moms live now' },
    { color: 'rgb(136,135,128)', name: 'Screen time limits — strategies that hold', sub: 'School Age · Starting in 20 min' },
    { color: 'rgb(136,135,128)', name: "Kids' anxiety — when to get help", sub: 'Support · Tomorrow 8pm ET' },
  ],
  teen: [
    { color: 'rgb(155,41,66)', name: 'Teen mental health — open conversation', sub: 'Teen Parents · 8 moms live now' },
    { color: 'rgb(155,41,66)', name: 'How to talk to your teenager', sub: 'Teen Parents · 5 moms live now' },
    { color: 'rgb(136,135,128)', name: 'Social media — setting boundaries that work', sub: 'Teen Parents · Starting in 25 min' },
    { color: 'rgb(136,135,128)', name: 'College prep — the emotional side', sub: 'Teen Parents · Tomorrow 7pm ET' },
  ],
};

const DISCOVER_ACTIVITIES = {
  newborn: [
    { emoji: '📚', name: 'Baby Lap Time — Columbus Library', desc: 'Songs & stories for 0–12 months · Saturdays 10am · Free', age: '0–3 mo' },
    { emoji: '🤸', name: 'Mommy & Me Yoga — Clintonville', desc: '6 weeks–crawling · Tues & Thurs · $15/class', age: '0–3 mo' },
    { emoji: '🎵', name: 'Infant Music Together — Short North', desc: '0–18 months · weekly classes · builds language', age: '0–12 mo' },
    { emoji: '🌿', name: 'New Moms Walking Group — Schiller Park', desc: 'Every Wed 9am · strollers welcome · free', age: '0–6 mo' },
  ],
  infant: [
    { emoji: '🏊', name: 'Parent & Baby Swim — Ohio State Rec', desc: '6 months+ · Sat & Sun mornings · $12/class', age: '6–12 mo' },
    { emoji: '📚', name: 'Toddler Story Time — Dublin Library', desc: '9–18 months · Fri 10am · Free', age: '9–18 mo' },
    { emoji: '🎵', name: 'Kindermusik Classes — Westerville', desc: 'Infant–toddler · builds musical & language skills', age: '0–18 mo' },
    { emoji: '🤸', name: 'Baby Sensory Play — Hilliard', desc: '6–12 months · Wednesday mornings · $10', age: '6–12 mo' },
  ],
  toddler: [
    { emoji: '🎨', name: 'Toddler Art Class — Short North Arts', desc: '18 months–3 years · Wed mornings · $20/class', age: '1–3 yr' },
    { emoji: '🤸', name: 'Tiny Tumblers — Gymboree Easton', desc: 'Walking–3 years · structured play + movement', age: '1–3 yr' },
    { emoji: '📚', name: 'Toddler Story Time + Craft — Bexley', desc: 'Ages 2–4 · Tues 10am · Free', age: '2–4 yr' },
    { emoji: '🌿', name: 'Parent & Toddler Nature Walk — Hocking Hills', desc: 'Monthly Saturdays · stroller-friendly trails', age: '1–3 yr' },
  ],
  preschool: [
    { emoji: '⚽', name: 'Little Kickers Soccer — Hilliard', desc: 'Ages 3–5 · Saturday mornings · intro to sports', age: '3–5 yr' },
    { emoji: '🎭', name: 'Preschool Drama Class — Available', desc: 'Ages 4–6 · builds confidence & creativity', age: '4–6 yr' },
    { emoji: '🏊', name: 'Swim Lessons — Columbus Swim', desc: 'Ages 3+ · group lessons · safety first', age: '3+ yr' },
    { emoji: '⛺', name: 'YMCA Summer Day Camps', desc: 'Ages 5+ · Registration opens March · Full day options', age: '5+ yr' },
  ],
  school: [
    { emoji: '⛺', name: 'YMCA Summer Camps 2026', desc: 'Ages 6–12 · Multiple themes · Full & half day', age: '6–12 yr' },
    { emoji: '🎨', name: 'Columbus Museum of Art Classes', desc: 'Ages 6+ · Saturday workshops · drop-off option', age: '6–12 yr' },
    { emoji: '💻', name: 'Code Ninjas — Dublin', desc: 'Ages 7–14 · coding camps & after-school', age: '7–14 yr' },
    { emoji: '⚽', name: 'Columbus Club Soccer — Tryouts', desc: 'Ages 8–12 · Spring registration open', age: '8–12 yr' },
  ],
  teen: [
    { emoji: '🎓', name: 'OSU Pre-College Programs', desc: 'Ages 14–17 · Summer academic programs · college prep', age: '14–17 yr' },
    { emoji: '💼', name: 'Teen Volunteer Columbus', desc: 'Ages 13+ · resume building · community impact', age: '13+ yr' },
    { emoji: '🎨', name: 'Columbus College of Art — Teen Programs', desc: 'Ages 13–18 · summer intensives & workshops', age: '13–18 yr' },
    { emoji: '🏃', name: 'LifeTime Fitness Teen Memberships', desc: 'Ages 13+ · fitness + social + structured activities', age: '13+ yr' },
  ],
};

const GROW_PATH_BASE = {
  newborn: [
    { title: 'Local activities — just for newborns', desc: "Baby lap times, mommy & me classes, infant music — Nova finds what's near you and age-appropriate so you're never searching from scratch.", nova: 'Nova finds activities near you →' },
    { title: 'Return to work planning', desc: 'Leave timeline, childcare research, pumping at work, the emotional weight of going back — Nova helps you navigate on your terms.', nova: 'Nova holds the logistics with you →' },
    { title: "Your recovery, not just baby's milestones", desc: '6-week OB visit prep, pelvic floor PT referral, mental health check-ins — Nova ensures this season is about you too.', nova: 'Nova checks in on you →' },
    { title: 'Building your mom community', desc: "New mom groups near you, walking buddies, Nova Spaces matched to your baby's age — so you're not isolated in the fourth trimester.", nova: 'Nova matches you to your people →' },
    { title: "Planning for another baby (when you're ready)", desc: 'Nova carries your full history forward — your birth, your body, your timeline — and transitions you back to TTC or Fertility when the time comes.', nova: 'Nova carries your story forward →' },
  ],
  toddler: [
    { title: 'Local activities for toddlers', desc: "Toddler gym classes, art, story time, playgroups near you — Nova finds what's developmental for your child's exact age.", nova: 'Nova finds activities near you →' },
    { title: 'Working mom support', desc: 'Daycare research, preschool logistics, career navigation, the mental load — Nova helps you hold it all without drowning.', nova: 'Nova carries the mental load →' },
    { title: 'Age-matched parenting guidance', desc: "Tantrums, potty training, language development, sleep transitions — Nova delivers guidance specific to what you're navigating right now.", nova: "Nova knows your child's stage →" },
    { title: 'Building community as a toddler parent', desc: 'Playdate matching, toddler parent groups, local mom communities — Nova connects you to women at the same stage.', nova: 'Nova matches you to your people →' },
    { title: 'Planning for another baby', desc: "Spacing, your body's readiness, telling your toddler — Nova carries your history forward when you're ready.", nova: 'Nova carries your story forward →' },
  ],
  teen: [
    { title: 'Local opportunities for your teen', desc: "Volunteer programs, pre-college camps, arts workshops, sports tryouts near Columbus — Nova finds opportunities matched to your teen's interests and age.", nova: 'Nova finds opportunities near you →' },
    { title: 'Working mom support — the teen years', desc: 'Teens need you differently now — less logistics, more availability. Nova helps you navigate the shift from manager to mentor while maintaining your career.', nova: 'Nova holds both sides →' },
    { title: 'Teen parenting guidance', desc: 'Communication, mental health, social media, college prep — Nova delivers guidance for the years most parenting apps have abandoned you.', nova: 'Nova stays through the teen years →' },
    { title: 'Your identity and what comes next', desc: "As your teen gains independence, Nova helps you reconnect with who you are outside of motherhood — relationships, career, what's next for you.", nova: "Nova remembers you're still you →" },
    { title: 'Empty nest preparation', desc: 'The transition to college and beyond — Nova helps you prepare emotionally, practically, and for the new relationship with your adult child.', nova: "Nova prepares you for what's next →" },
  ],
};

const GROW_PATH = {
  ...GROW_PATH_BASE,
  infant: GROW_PATH_BASE.toddler,
  preschool: GROW_PATH_BASE.toddler,
  school: GROW_PATH_BASE.toddler,
};

// ── MAIN COMPONENT ───────────────────────────────────────────────

export default function MotherhoodChapterPage() {
  const [selectedAge, setSelectedAge] = useState('newborn');
  const accent = 'rgb(155, 41, 66)';
  const accentLight = 'rgba(155, 41, 66, 0.1)';
  const accentBorder = 'rgba(155, 41, 66, 0.4)';

  const sectionWhite = { padding: '52px 48px', backgroundColor: 'rgb(255,252,249)' };
  const sectionCream = { padding: '52px 48px', backgroundColor: 'rgb(249,246,241)' };
  const sectionAnchor = { scrollMarginTop: '80px' };
  const inner = { maxWidth: '1100px', margin: '0 auto' };
  const commRowStyle = { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', borderBottom: '0.5px solid rgb(232,224,216)' };

  const recover = RECOVER_CONTENT[selectedAge] || RECOVER_CONTENT.newborn;
  const track = TRACK_CONTENT[selectedAge] || TRACK_CONTENT.newborn;
  const spaces = CONNECT_SPACES[selectedAge] || CONNECT_SPACES.newborn;
  const liveRooms = LIVE_ROOMS[selectedAge] || LIVE_ROOMS.newborn;
  const activities = DISCOVER_ACTIVITIES[selectedAge] || DISCOVER_ACTIVITIES.newborn;
  const growPath = GROW_PATH[selectedAge] || GROW_PATH.toddler;

  const ageLabel = AGE_RANGES.find((a) => a.id === selectedAge)?.label || '0–3 months';

  return (
    <div style={{ background: 'rgb(255,252,249)' }}>
      <div style={{ background: 'var(--white)', color: 'var(--text)' }}>
        <LandingNav />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', minHeight: '480px' }}>
        <div
          style={{
            backgroundImage: "url('/images/chapter-heroes/postpartum.png')",
            backgroundSize: 'cover',
            backgroundPosition: 'top center',
            minHeight: '480px',
          }}
        />
        <div style={{ background: accent, padding: '52px 48px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.15)', borderRadius: '20px', padding: '5px 12px', marginBottom: '16px', width: 'fit-content' }}>
            <span style={{ fontSize: '16px' }}>👶</span>
            <span style={{ fontSize: '12px', fontWeight: 600, color: 'white' }}>Motherhood</span>
            <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.7)', background: 'rgba(255,255,255,0.15)', padding: '2px 8px', borderRadius: '10px' }}>Coming 2026</span>
          </div>
          <h1 className="serif" style={{ fontSize: 'clamp(28px, 3vw, 40px)', fontWeight: 700, color: 'white', lineHeight: 1.2, marginBottom: '16px' }}>
            Parenting platforms track what your baby is doing.{' '}
            <em style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)' }}>OrivonAI tracks how you&apos;re doing.</em>
          </h1>
          <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.8)', lineHeight: 1.65, marginBottom: '24px', maxWidth: '440px' }}>
            Postpartum recovery tools, milestone tracking, mental health check-ins, community matched to your child&apos;s exact age, local activity discovery, and working mom support — from the fourth trimester through the teen years, across every stage most platforms abandon you in.
          </p>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" style={{ background: 'white', color: accent, fontSize: '14px', fontWeight: 700, padding: '11px 22px', borderRadius: '999px', border: 'none', cursor: 'pointer' }}>Join the waitlist</button>
          </div>
        </div>
      </div>

      <div style={{ background: 'rgb(249,246,241)', padding: '14px 48px', borderBottom: '1px solid rgb(232,224,216)', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '11px', fontWeight: 700, color: 'rgb(122,107,99)', textTransform: 'uppercase', letterSpacing: '0.08em', flexShrink: 0 }}>Your child&apos;s age:</span>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {AGE_RANGES.map((range) => (
            <button
              key={range.id}
              type="button"
              onClick={() => setSelectedAge(range.id)}
              style={{
                fontSize: '11px',
                fontWeight: selectedAge === range.id ? 700 : 600,
                padding: '5px 12px',
                borderRadius: '20px',
                background: selectedAge === range.id ? accent : 'rgb(255,253,249)',
                color: selectedAge === range.id ? 'white' : 'rgb(122,107,99)',
                border: selectedAge === range.id ? 'none' : '0.5px solid rgb(232,224,216)',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                fontFamily: 'inherit',
              }}
            >
              {range.emoji} {range.label}
            </button>
          ))}
        </div>
      </div>

      <div id="chapter-recover" style={{ ...sectionWhite, ...sectionAnchor }}>
        <div style={inner}>
          <SectionHeader
            act="Recover"
            headline="Every app tracks the baby."
            italicLine="Nova checks in on you."
            subhead="The fourth trimester is one of the hardest transitions of your life. Nova shows up for your recovery — physical, emotional, and mental — not just the feeding schedule."
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: '24px', alignItems: 'stretch' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                { emoji: '💚', title: 'Postpartum recovery tracker', desc: "Physical recovery, healing, pelvic floor — Nova checks in on your body week by week and flags what's worth mentioning to your OB.", isNew: true },
                { emoji: '🧠', title: 'Mental health check-ins', desc: "PPD, PPA, birth trauma, intrusive thoughts — Nova checks in proactively, normalizes what's normal, and escalates when it's time to get support.", isNew: true },
                { emoji: '🍼', title: 'Feeding support — breast & bottle', desc: 'Latch struggles, supply questions, pumping schedules, formula decisions — Nova holds all of it without judgment.', isNew: false },
                { emoji: '💼', title: 'Return to work planning', desc: 'Leave timeline, childcare research, pumping at work, the emotional weight of going back — Nova helps you navigate on your terms.', isNew: true },
                { emoji: '💑', title: 'Relationship & identity support', desc: 'Partnership dynamics, matrescence, who you are as a mother and who you still are outside of it. Nova holds both.', isNew: true },
              ].map((f) => (
                <FeatCard key={f.title} {...f} borderColor={accentBorder} />
              ))}
            </div>
            <div style={{ background: 'rgb(249,246,241)', borderRadius: '16px', padding: '20px', border: '0.5px solid rgb(232,224,216)', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <p style={{ fontSize: '11px', fontWeight: 700, color: 'rgb(122,107,99)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Nova&apos;s check-in — {ageLabel}
              </p>
              <InsightCard title={`👶 ${recover.childName} · ${recover.childAge} · ${recover.childBorn}`} desc={recover.childDetails} />
              <InsightCard title="💪 Your physical recovery" desc={recover.recovery} />
              <InsightCard title={`🧠 How ${recover.oraName} has been feeling`} desc={recover.feeling} />
              <InsightCard title="💡 Nova&apos;s note this week" desc={recover.note} tinted />
              <div style={{ background: 'white', borderRadius: '10px', border: '0.5px solid rgb(232,224,216)', overflow: 'hidden' }}>
                <div style={{ padding: '8px 12px', borderBottom: '1px solid rgb(232,224,216)', fontSize: '11px', fontWeight: 700, color: 'rgb(122,107,99)' }}>📄 Nova suggests reading</div>
                {recover.articles.map((a, i) => (
                  <div key={a} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', borderBottom: i < recover.articles.length - 1 ? '0.5px solid rgb(232,224,216)' : 'none' }}>
                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: accent, flexShrink: 0 }} />
                    <div>
                      <p style={{ fontSize: '12px', fontWeight: 500, color: 'rgb(17,17,17)', margin: 0 }}>{a}</p>
                      <p style={{ fontSize: '10px', color: 'rgb(122,107,99)', marginTop: '1px', marginBottom: 0 }}>Clinically reviewed · 4–6 min read</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ height: '1px', background: 'rgb(232,224,216)' }} />

      <div id="chapter-track" style={{ ...sectionCream, ...sectionAnchor }}>
        <div style={inner}>
          <SectionHeader
            act="Track"
            headline="Your child's milestones."
            italicLine="And how you're doing too."
            subhead={`Nova tracks what matters at ${ageLabel} — flags what's worth a pediatrician call, and never forgets you're in the picture too.`}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '24px', alignItems: 'stretch' }}>
            <div style={{ background: 'rgb(255,253,249)', border: '0.5px solid rgb(232,224,216)', borderRadius: '16px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '12px 16px', borderBottom: `1.5px solid ${accent}`, display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                <NovaAvatarImg size={30} />
                <div>
                  <p style={{ fontSize: '12px', fontWeight: 600, color: 'rgb(17,17,17)', margin: 0 }}>Nova — your OrivonAI companion</p>
                  <p style={{ fontSize: '10px', color: 'rgb(122,107,99)', margin: 0 }}>
                    Motherhood · {ageLabel} · Available 24/7
                  </p>
                </div>
                <div style={{ marginLeft: 'auto', width: '7px', height: '7px', borderRadius: '50%', background: accent }} />
              </div>
              <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '10px', flexShrink: 0 }}>
                {track.chat.map((msg, i) =>
                  msg.from === 'nova' ? (
                    <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                      <NovaAvatarImg size={24} />
                      <div style={{ background: 'white', border: '0.5px solid rgb(232,224,216)', borderRadius: '0 10px 10px 10px', padding: '9px 12px', fontSize: '12px', color: 'rgb(17,17,17)', lineHeight: 1.5 }}>{msg.text}</div>
                    </div>
                  ) : (
                    <div key={i} style={{ background: accent, borderRadius: '10px 0 10px 10px', padding: '9px 12px', fontSize: '12px', color: 'white', lineHeight: 1.5, maxWidth: '75%', alignSelf: 'flex-end' }}>
                      {msg.text}
                    </div>
                  )
                )}
              </div>
              <div style={{ flex: 1 }} />
              <div style={{ padding: '8px 16px', display: 'flex', gap: '6px', flexWrap: 'wrap', borderTop: '0.5px solid rgb(232,224,216)', flexShrink: 0 }}>
                {track.pills.map((p) => (
                  <span key={p} style={{ fontSize: '10px', color: 'rgb(125,30,52)', border: '1px solid rgba(155,41,66,0.35)', borderRadius: '20px', padding: '4px 10px', background: 'white', whiteSpace: 'nowrap' }}>
                    {p}
                  </span>
                ))}
              </div>
              <div style={{ padding: '12px 16px', borderTop: '0.5px solid rgb(232,224,216)', display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                <span style={{ flex: 1, fontSize: '13px', color: 'rgb(136,135,128)' }}>Ask Nova anything about your journey…</span>
                <span style={{ background: accent, color: 'white', fontSize: '11px', fontWeight: 700, padding: '7px 14px', borderRadius: '20px', whiteSpace: 'nowrap', cursor: 'pointer' }}>Open In Nova</span>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                { emoji: '😴', title: 'Sleep tracker & regression alerts', desc: "Log sleep, track patterns, and get warned before regressions hit — not after. Nova preps you for what's coming.", isNew: true },
                { emoji: '📏', title: 'Growth & milestone tracker', desc: 'Weight, height, developmental milestones — Nova tracks the curve and flags anything worth mentioning at your next pediatric visit.', isNew: false },
                { emoji: '🤔', title: '"Is this normal?" — age-aware', desc: "Rashes, behavior changes, feeding patterns — Nova answers relative to your child's exact age and developmental stage.", isNew: true },
                { emoji: '💉', title: 'Vaccine & well-visit scheduler', desc: 'Pediatric visit reminders, vaccine schedules, and what to expect at each well-child visit — Nova preps you before every appointment.', isNew: true },
              ].map((f) => (
                <FeatCard key={f.title} {...f} borderColor={accentBorder} />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div style={{ height: '1px', background: 'rgb(232,224,216)' }} />

      <div id="chapter-connect" style={{ ...sectionWhite, ...sectionAnchor }}>
        <div style={inner}>
          <SectionHeader
            act="Connect"
            headline="The community that's always there."
            italicLine="Not a Reddit thread from last year."
            subhead={`Nova matches you to mothers at your exact stage — same child age, same situation — and opens live rooms for the moments that can't wait.`}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', alignItems: 'stretch' }}>
            <div style={{ background: 'rgb(255,253,249)', border: '0.5px solid rgb(232,224,216)', borderRadius: '14px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <CardHead title="Nova Spaces" sub={`Matched to ${ageLabel}`} right={<span style={{ fontSize: '11px', color: accent }}>See all</span>} />
              {spaces.map((s, i) => (
                <div key={s.name} style={{ ...commRowStyle, borderBottom: i < spaces.length - 1 ? '0.5px solid rgb(232,224,216)' : 'none' }}>
                  <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: accentLight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', flexShrink: 0 }}>{s.emoji}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: '13px', fontWeight: 500, color: 'rgb(17,17,17)', margin: 0 }}>{s.name}</p>
                    <p style={{ fontSize: '11px', color: 'rgb(122,107,99)', marginTop: '1px', marginBottom: 0 }}>{s.desc}</p>
                  </div>
                  <span style={{ fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px', background: s.tagBg, color: s.tagColor, whiteSpace: 'nowrap', flexShrink: 0 }}>{s.tag}</span>
                </div>
              ))}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '14px 16px', background: `linear-gradient(120deg, ${accentLight}, rgba(251,234,240,0.85))`, borderTop: `1.5px solid ${accent}`, flex: 1 }}>
                <NovaAvatarImg size={24} />
                <div>
                  <p style={{ fontSize: '12px', fontWeight: 600, color: 'rgb(125,30,52)', marginBottom: '3px' }}>Nova is active in every space</p>
                  <p style={{ fontSize: '11px', color: 'rgb(90,35,50)', lineHeight: 1.55, margin: 0 }}>She reads every thread in real time — surfacing clinical articles, redirecting spirals, and correcting misinformation before it spreads.</p>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ background: 'rgb(255,253,249)', border: '0.5px solid rgb(232,224,216)', borderRadius: '14px', overflow: 'hidden' }}>
                <CardHead
                  title="Nova Live Rooms"
                  sub="Happening right now"
                  right={
                    <span style={{ background: accent, color: 'white', fontSize: '9px', fontWeight: 700, padding: '2px 6px', borderRadius: '10px', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                      <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: 'white', display: 'inline-block' }} />
                      Live
                    </span>
                  }
                />
                {liveRooms.map((r, i) => (
                  <div key={r.name} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', borderBottom: i < liveRooms.length - 1 ? '0.5px solid rgb(232,224,216)' : 'none' }}>
                    <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: r.color, flexShrink: 0 }} />
                    <div>
                      <p style={{ fontSize: '13px', fontWeight: 500, color: 'rgb(17,17,17)', margin: 0 }}>{r.name}</p>
                      <p style={{ fontSize: '11px', color: 'rgb(122,107,99)', marginTop: '1px', marginBottom: 0 }}>{r.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ background: 'rgb(255,253,249)', border: '0.5px solid rgb(232,224,216)', borderRadius: '14px', overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <CardHead
                  title={
                    <>
                      Nova Connections{' '}
                      <span style={{ fontSize: '9px', fontWeight: 700, background: 'rgb(245,234,237)', color: 'rgb(125,30,52)', padding: '1px 6px', borderRadius: '8px', marginLeft: '6px' }}>New</span>
                    </>
                  }
                  sub={`Same stage · ${ageLabel} · Nova-matched`}
                  right={<span style={{ fontSize: '11px', color: accent }}>See all</span>}
                />
                {[
                  {
                    initials: 'JL',
                    bg: accent,
                    name: 'Jess L.',
                    desc: selectedAge === 'newborn' ? '7-week-old · C-section · returning to work soon' : selectedAge === 'teen' ? 'Daughter 16 · college prep · working mom' : 'Same age child · similar situation',
                    match: '98%',
                  },
                  {
                    initials: 'MK',
                    bg: 'rgb(127,119,221)',
                    name: 'Maya K.',
                    desc: selectedAge === 'newborn' ? '5-week-old · breastfeeding · Columbus, OH' : selectedAge === 'teen' ? 'Son 15 · anxiety support · open to chat' : 'Local mom · matched by Nova',
                    match: '94%',
                  },
                  {
                    initials: 'SR',
                    bg: 'rgb(201,104,126)',
                    name: 'Sara R.',
                    desc: selectedAge === 'newborn' ? '8-week-old · PPD support · IVF graduate' : selectedAge === 'teen' ? 'Twins 14 · navigating social media' : 'Matched by child age and stage',
                    match: '91%',
                  },
                ].map((c, i) => (
                  <div key={c.name} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px', borderBottom: i < 2 ? '0.5px solid rgb(232,224,216)' : 'none' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: c.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: 'white', flexShrink: 0 }}>{c.initials}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: '13px', fontWeight: 500, color: 'rgb(17,17,17)', margin: 0 }}>{c.name}</p>
                      <p style={{ fontSize: '11px', color: 'rgb(122,107,99)', marginTop: '1px', marginBottom: 0 }}>{c.desc}</p>
                    </div>
                    <span style={{ fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px', background: 'rgb(245,234,237)', color: 'rgb(125,30,52)', whiteSpace: 'nowrap', flexShrink: 0 }}>{c.match}</span>
                    <span style={{ fontSize: '11px', fontWeight: 600, color: accent, border: `0.5px solid ${accent}`, borderRadius: '20px', padding: '3px 9px', whiteSpace: 'nowrap', flexShrink: 0, marginLeft: '4px', cursor: 'pointer' }}>Connect</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ height: '1px', background: 'rgb(232,224,216)' }} />

      <div id="chapter-grow" style={{ ...sectionCream, ...sectionAnchor }}>
        <div style={inner}>
          <SectionHeader
            act="Grow"
            headline="Discover what's next."
            italicLine="For your child and for you."
            subhead={`Age-matched activities near Columbus, working mom resources, parenting guidance — and when you're ready, help with what comes next. Nova grows with your family.`}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'stretch' }}>
            <div style={{ background: 'rgb(255,253,249)', border: '0.5px solid rgb(232,224,216)', borderRadius: '16px', padding: '20px', display: 'flex', flexDirection: 'column' }}>
              <p style={{ fontSize: '11px', fontWeight: 700, color: 'rgb(122,107,99)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px' }}>What Nova helps you discover</p>
              {growPath.map((step, i) => (
                <div key={step.title} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', padding: '11px 0', borderBottom: i < growPath.length - 1 ? '0.5px solid rgb(232,224,216)' : 'none' }}>
                  <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: accentLight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, color: accent, flexShrink: 0, marginTop: '1px' }}>{i + 1}</div>
                  <div>
                    <p style={{ fontSize: '13px', fontWeight: 600, color: 'rgb(17,17,17)', marginBottom: '2px' }}>{step.title}</p>
                    <p style={{ fontSize: '12px', color: 'rgb(95,94,90)', lineHeight: 1.45, margin: 0 }}>{step.desc}</p>
                    <p style={{ fontSize: '11px', color: accent, marginTop: '3px', fontStyle: 'italic', marginBottom: 0 }}>{step.nova}</p>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ background: 'rgb(255,253,249)', border: '0.5px solid rgb(232,224,216)', borderRadius: '14px', overflow: 'hidden' }}>
                <CardHead
                  title={
                    <>
                      Discover near you{' '}
                      <span style={{ fontSize: '9px', fontWeight: 700, background: 'rgb(245,234,237)', color: 'rgb(125,30,52)', padding: '1px 6px', borderRadius: '8px', marginLeft: '6px' }}>New</span>
                    </>
                  }
                  sub={`Columbus, OH · ${ageLabel} · Nova-matched`}
                  right={<span style={{ fontSize: '11px', color: accent }}>See all</span>}
                />
                {activities.map((a, i) => (
                  <div key={a.name} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', borderBottom: i < activities.length - 1 ? '0.5px solid rgb(232,224,216)' : 'none' }}>
                    <span style={{ fontSize: '20px', flexShrink: 0 }}>{a.emoji}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: '13px', fontWeight: 500, color: 'rgb(17,17,17)', margin: 0 }}>{a.name}</p>
                      <p style={{ fontSize: '11px', color: 'rgb(122,107,99)', marginTop: '1px', marginBottom: 0 }}>{a.desc}</p>
                    </div>
                    <span style={{ fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px', background: 'rgba(155,41,66,0.12)', color: 'rgb(125,30,52)', whiteSpace: 'nowrap', flexShrink: 0 }}>{a.age}</span>
                  </div>
                ))}
              </div>
              {[
                { emoji: '🗺️', title: 'Local activity finder — age-matched', desc: "Classes, camps, library programs, playgroups near you — Nova finds them by your child's exact age so you're never searching from scratch.", isNew: true },
                { emoji: '💼', title: 'Working mom hub', desc: 'Childcare research, school logistics, career navigation, the mental load — held by Nova so you can focus on what matters.', isNew: true },
              ].map((f) => (
                <FeatCard key={f.title} {...f} borderColor={accentBorder} />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div style={{ background: accent, padding: '52px 48px', textAlign: 'center' }}>
        <p style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', marginBottom: '10px' }}>Start your motherhood journey</p>
        <h2 className="serif" style={{ fontSize: 'clamp(24px, 2.5vw, 30px)', fontWeight: 700, color: 'white', lineHeight: 1.25, marginBottom: '10px' }}>
          {"I'm here for you and your family."}
          <br />
          <em style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.85)' }}>From newborn to teen years and everything between.</em>
        </h2>
        <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.8)', maxWidth: '480px', margin: '0 auto 24px', lineHeight: 1.6 }}>
          Recovery, milestones, community, discovery — Nova grows with your family and never makes you start over.
        </p>
        <button type="button" style={{ background: 'white', color: accent, fontSize: '13px', fontWeight: 700, padding: '11px 28px', borderRadius: '999px', border: 'none', cursor: 'pointer' }}>Start Free With Nova</button>
      </div>

      <SiteFooter linkColor="var(--muted)" />
    </div>
  );
}
